/**
 * Processes filter values and returns updated filter array
 * @param prevFilters Current filter values array
 * @param field Field name to update
 * @param value New value (string or string[])
 * @returns Updated filter values array
 */
export const processFilterUpdate = (
  prevFilters: Array<{ key: string; value: string }>,
  field: string,
  value: string | string[]
): Array<{ key: string; value: string }> => {
  // Keys arrive in mixed case (default BU_CODE vs config field bu_code); both mean the same column.
  const filteredArray = prevFilters.filter(
    filter => filter.key.toUpperCase() !== field.toUpperCase()
  );

  // Check if the value already contains the field name and operator
  // This indicates it's already formatted
  if (
    typeof value === 'string' &&
    (value.startsWith(`${field}.`) || value.startsWith(`${field}(`))
  ) {
    return [...filteredArray, { key: field, value: value }];
  }

  // Process the value
  let processedValue: string | null = null;

  if (Array.isArray(value) && value.length > 0) {
    processedValue = value.join('|');
  }
  // If value is a string, use it directly
  else if (typeof value === 'string' && value !== '') {
    processedValue = value;
  }

  // Add the filter if we have a valid processed value
  if (processedValue) {
    // Check if the value already has any operator format (.eq, .gt, .gte, .lt, .lte, .like)
    const hasOperator = /\.(eq|gt|gte|lt|lte|like)\(/.test(processedValue);

    // Choose operator based on field name
    let operator = 'eq'; // Default operator

    // For ARTNAME field, use 'like' operator and wrap value in % wildcards
    if (
      field === 'ARTNAME' ||
      field === 'AVIN_1' ||
      field === 'AVIN_2' ||
      field === 'AVIN_3' ||
      field === 'SLID'
    ) {
      operator = 'like';

      // Only add wildcards if the value doesn't already have them
      if (!processedValue.startsWith('%') && !processedValue.endsWith('%')) {
        // Handle pipe-separated values
        if (processedValue.includes('|')) {
          // Split by pipe, add wildcards to each value, then rejoin
          processedValue = processedValue
            .split('|')
            .map(val => `%${val}%`)
            .join('|');
        } else {
          // Single value, just wrap it
          processedValue = `%${processedValue}%`;
        }
      }
    }

    // Format as expression if not already formatted
    const formattedValue = hasOperator
      ? processedValue
      : `${field}.${operator}(${processedValue})`;

    return [...filteredArray, { key: field, value: formattedValue }];
  }

  return filteredArray;
};
